import { ParsedUrlQuery } from 'querystring'
import { createFilterOptions } from '@mui/material/useAutocomplete'
import { Filter, onChangeFunc } from 'components/core/search-filter/filter'
import { getFilterKeyToLabelMap } from 'components/core/task/ContextualTaskSearchFilter'
import { StepApprovalTypeEnumToString } from 'components/pages/policy/type'
import { StepApprovalType } from 'pbts/c1/api/policy/v1/policy_steps_approval'
import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'

export const StepApprovalTypeQueryParam = 'stepApprovalType'

type ApprovalTypeFilterProps = {
    selected: StepApprovalType[] | undefined
    onChange: onChangeFunc<StepApprovalType>
}

const getOptionSelected = (option1: StepApprovalType, option2: StepApprovalType): boolean => {
    return option1 === option2
}

export const ApprovalTypeFilter = ({ selected, onChange }: ApprovalTypeFilterProps): React.ReactElement => {
    const intl = useIntl()
    const filterOptions = createFilterOptions<StepApprovalType>({})
    const filterKeyToLabel = getFilterKeyToLabelMap(intl)

    const approvalTypes: StepApprovalType[] = useMemo(() => {
        const types: StepApprovalType[] = []
        for (const typ in StepApprovalType) {
            if ((StepApprovalType[typ] as unknown as number) > 0) {
                types.push(StepApprovalType[typ] as unknown as StepApprovalType)
            }
        }
        return types
    }, [])

    const renderLabel = useCallback((option: StepApprovalType): string => StepApprovalTypeEnumToString(option), [])

    // Load initial types from query.
    const fromQuery = useCallback((query: ParsedUrlQuery): StepApprovalType[] => {
        let types = query[StepApprovalTypeQueryParam]
        if (!types) {
            return []
        }

        if (!Array.isArray(types)) {
            types = [types]
        }

        const parsedTypes: StepApprovalType[] = []
        types.forEach((typeStr) => {
            const typ = StepApprovalType[typeStr]
            if (!typ) {
                return
            }
            parsedTypes.push(typ)
        })

        return parsedTypes
    }, [])

    // Serialize selections to query.
    const toQuery = useCallback((query: ParsedUrlQuery, types: StepApprovalType[]): void => {
        const typeStrs: string[] = []
        types.forEach((typ) => {
            const typeStr = StepApprovalTypeEnumToString(typ)
            if (!typeStr) {
                return
            }
            typeStrs.push(typeStr)
        })

        if (typeStrs.length !== 0) {
            query[StepApprovalTypeQueryParam] = typeStrs
        } else {
            delete query[StepApprovalTypeQueryParam]
        }
    }, [])

    return <Filter<StepApprovalType>
        label={filterKeyToLabel['step-approval-types'] || ''}
        options={approvalTypes}
        selected={selected}
        fromQuery={fromQuery}
        toQuery={toQuery}
        onChange={onChange}
        filterOptions={filterOptions}
        getOptionSelected={getOptionSelected}
        renderLabel={renderLabel}
    />
}
